import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import BottomSheet from '../components/mobile/BottomSheet';

/**
 * Replaces window.alert / window.confirm with in-app toasts and a confirm sheet.
 *
 *   const { toast, confirm } = useDialog();
 *   toast('Saved');                          // success
 *   toast('Could not save', 'error');
 *   if (await confirm({ title: 'Delete?', destructive: true })) { ... }
 */
const DialogContext = createContext(null);

const TOAST_STYLES = {
  success: { Icon: CheckCircle, className: 'bg-gray-900 text-white', iconClass: 'text-green-400' },
  error: { Icon: AlertCircle, className: 'bg-red-600 text-white', iconClass: 'text-white' },
  info: { Icon: Info, className: 'bg-gray-900 text-white', iconClass: 'text-blue-300' },
};

export function DialogProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const nextId = useRef(0);

  const dismissToast = useCallback((id) => {
    setToasts(list => list.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((message, type = 'success', { duration = 3500 } = {}) => {
    const id = ++nextId.current;
    setToasts(list => [...list.slice(-2), { id, message, type }]);
    if (duration) setTimeout(() => dismissToast(id), duration);
    return id;
  }, [dismissToast]);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    setConfirmState({
      title: 'Are you sure?',
      message: '',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      destructive: false,
      ...(typeof opts === 'string' ? { message: opts } : opts),
      resolve,
    });
  }), []);

  const closeConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  return (
    <DialogContext.Provider value={{ toast, confirm }}>
      {children}

      {/* Toasts: above the bottom nav on mobile, bottom-right on desktop */}
      <div
        className="fixed z-[70] left-4 right-4 bottom-above-nav md:left-auto md:right-24 md:bottom-6 md:w-96 flex flex-col gap-2 pointer-events-none"
        aria-live="polite"
      >
        {toasts.map(t => {
          const style = TOAST_STYLES[t.type] || TOAST_STYLES.info;
          return (
            <div
              key={t.id}
              className={`pointer-events-auto flex items-start gap-3 rounded-xl shadow-lg px-4 py-3 animate-fade-in ${style.className}`}
              role={t.type === 'error' ? 'alert' : 'status'}
            >
              <style.Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${style.iconClass}`} />
              <p className="flex-1 text-sm whitespace-pre-line">{t.message}</p>
              <button
                type="button"
                onClick={() => dismissToast(t.id)}
                className="-mr-1 p-1 opacity-70 hover:opacity-100"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <BottomSheet
        open={Boolean(confirmState)}
        onClose={() => closeConfirm(false)}
        title={confirmState?.title}
        footer={
          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
            <button
              type="button"
              onClick={() => closeConfirm(false)}
              className="w-full md:w-auto px-4 py-3 md:py-2 rounded-xl md:rounded-md border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
            >
              {confirmState?.cancelLabel}
            </button>
            <button
              type="button"
              onClick={() => closeConfirm(true)}
              className={`w-full md:w-auto px-4 py-3 md:py-2 rounded-xl md:rounded-md text-white font-medium ${
                confirmState?.destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
              autoFocus
            >
              {confirmState?.confirmLabel}
            </button>
          </div>
        }
      >
        {confirmState?.message && (
          <p className="text-sm text-gray-600 whitespace-pre-line">{confirmState.message}</p>
        )}
      </BottomSheet>
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within DialogProvider');
  return ctx;
}
